// Pure game logic for Butterfly Path — a Corsi-style visual-spatial memory
// exercise. A guide butterfly flies a path over the flowers; the player then
// repeats the path by clicking the flowers in the same order. Everything here
// is side-effect free (randomness is injectable) so it can be unit tested.

// Defaults used when the player's level carries no (valid) parameters.
export const DEFAULT_PARAMS = {
  flowers: 8,
  startLength: 3,
  rounds: 5,
  flightMs: 1200,
};

// Never ask for a path shorter than this, however many rounds are missed.
export const MIN_PATH_LENGTH = 2;

// Board positions are percentages of the (square) board. Flowers are kept
// inside this margin so they never clip the edge, and the two corners are left
// free as the butterflies' "home" perches.
const EDGE_MARGIN = 10;
export const GUIDE_HOME = { x: 6, y: 6 };
export const PLAYER_HOME = { x: 94, y: 94 };

// Parse a level's parameters from its level_parameters JSON, e.g.
//   {"flowers": 8, "startLength": 3, "rounds": 5, "flightMs": 1200}
// Missing or invalid fields fall back to DEFAULT_PARAMS; the path length is
// clamped so it always fits on the board. Returns DEFAULT_PARAMS for an absent
// or malformed level.
export const parseLevelParams = (level) => {
  let p = {};
  if (level && level.level_parameters) {
    try {
      p = JSON.parse(level.level_parameters) || {};
    } catch (e) {
      p = {};
    }
  }
  const pick = (key, min, max) => {
    const v = p[key];
    return Number.isInteger(v) && v >= min && v <= max ? v : DEFAULT_PARAMS[key];
  };
  const flowers = pick("flowers", 4, 16);
  const rounds = pick("rounds", 1, 20);
  const flightMs = pick("flightMs", 300, 5000);
  const startLength = Math.min(pick("startLength", MIN_PATH_LENGTH, 15), flowers);
  return { flowers, startLength, rounds, flightMs };
};

// Place `count` flowers on a jittered grid so they never overlap and are
// spread over the whole board. Returns [{id, x, y}] with x/y in percent.
// The corner cells holding the butterflies' homes are skipped.
export const placeFlowers = (count, rng = Math.random) => {
  const cols = Math.ceil(Math.sqrt(count + 2));
  const rows = Math.ceil((count + 2) / cols);
  const cellW = (100 - 2 * EDGE_MARGIN) / cols;
  const cellH = (100 - 2 * EDGE_MARGIN) / rows;

  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const isGuideHome = r === 0 && c === 0;
      const isPlayerHome = r === rows - 1 && c === cols - 1;
      if (!isGuideHome && !isPlayerHome) cells.push({ r, c });
    }
  }
  // Fisher-Yates shuffle, then take the first `count` cells.
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }
  return cells.slice(0, count).map(({ r, c }, id) => ({
    id,
    // Jitter within the middle 50% of the cell so neighbours stay apart.
    x: EDGE_MARGIN + cellW * (c + 0.25 + rng() * 0.5),
    y: EDGE_MARGIN + cellH * (r + 0.25 + rng() * 0.5),
  }));
};

// Pick a path of `length` distinct flower ids. Distinct flowers keep the path
// unambiguous (a revisit would be impossible to "see" on the board).
export const generatePath = (flowers, length, rng = Math.random) => {
  const ids = flowers.map((f) => f.id);
  const n = Math.min(length, ids.length);
  const path = [];
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rng() * (ids.length - i));
    [ids[i], ids[j]] = [ids[j], ids[i]];
    path.push(ids[i]);
  }
  return path;
};

// Adaptive difficulty within a game: a perfect round lengthens the next path,
// a missed round shortens it — bounded by MIN_PATH_LENGTH and the flower count.
export const nextPathLength = (length, success, maxLength) =>
  success
    ? Math.min(length + 1, maxLength)
    : Math.max(length - 1, MIN_PATH_LENGTH);

// Session score = percent of rounds recalled perfectly (0-100), matching the
// percent-correct thresholds the level promotion logic expects.
export const scoreGame = (roundsCorrect, roundsPlayed) =>
  roundsPlayed > 0 ? Math.round((roundsCorrect / roundsPlayed) * 100) : 0;

// Heading in degrees from one point to another (screen coordinates, so
// positive is clockwise — matching CSS rotate()).
export const headingDegrees = (from, to) =>
  (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;

// Choose the rotation equivalent to `target` that is closest to `current`, so
// the butterfly always turns the short way round instead of spinning ~360°.
export const shortestRotation = (current, target) => {
  const delta = (((target - current) % 360) + 540) % 360 - 180;
  return current + delta;
};
